<!-- SPEC 33 / P37-8 — Gate de confirmação de perfil (e-mail + dados pessoais) -->
<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useTracking } from 'dashboard/composables/useTracking';
import AuthAPI from 'dashboard/api/auth';

const emit = defineEmits(['confirm']);

const currentUser = useMapGetter('getCurrentUser');
const { track } = useTracking();
const { t } = useI18n();

const dismissed = ref(false);
const resending = ref(false);

const emailUnconfirmed = computed(() => currentUser.value?.confirmed === false);
const profileIncomplete = computed(() => {
  const user = currentUser.value || {};
  const phone = user.custom_attributes?.phone_number || user.phone_number || '';
  return Boolean(user.name) === false || String(phone).trim() === '';
});

const reason = computed(() => {
  if (emailUnconfirmed.value) return 'email_unconfirmed';
  if (profileIncomplete.value) return 'incomplete';
  return null;
});

const visible = computed(() => Boolean(reason.value) && !dismissed.value);

const resendConfirmation = async () => {
  resending.value = true;
  try {
    await AuthAPI.resendConfirmation();
    track('profile_confirm_email_resent');
    useAlert(t('LAIS_ACCOUNT.CONFIRM.EMAIL_RESENT_SUCCESS'));
  } catch {
    useAlert(t('LAIS_ACCOUNT.CONFIRM.EMAIL_RESENT_ERROR'));
  } finally {
    resending.value = false;
  }
};

const confirmNow = () => {
  track('profile_confirm_gate_shown', { reason: reason.value });
  emit('confirm');
};
</script>

<template>
  <div
    v-show="visible"
    class="flex flex-col gap-2 rounded-lais border border-lais-gold bg-lais-warning/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
  >
    <div>
      <p class="text-[14px] font-semibold text-lais-warning">
        {{
          emailUnconfirmed
            ? t('LAIS_ACCOUNT.CONFIRM.EMAIL_TITLE')
            : t('LAIS_ACCOUNT.CONFIRM.PROFILE_TITLE')
        }}
      </p>
      <p class="text-[12px] text-lais-warning">
        {{
          emailUnconfirmed
            ? t('LAIS_ACCOUNT.CONFIRM.EMAIL_DESCRIPTION')
            : t('LAIS_ACCOUNT.CONFIRM.PROFILE_DESCRIPTION')
        }}
      </p>
    </div>
    <div class="flex items-center gap-2">
      <button
        v-if="emailUnconfirmed"
        type="button"
        :disabled="resending"
        class="rounded-lais bg-lais-warning px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-60"
        @click="resendConfirmation"
      >
        {{
          resending
            ? t('LAIS_ACCOUNT.CONFIRM.SENDING')
            : t('LAIS_ACCOUNT.CONFIRM.RESEND_EMAIL')
        }}
      </button>
      <button
        v-else
        type="button"
        class="rounded-lais bg-lais-warning px-3 py-1.5 text-[12px] font-semibold text-white"
        @click="confirmNow"
      >
        {{ t('LAIS_ACCOUNT.CONFIRM.CONFIRM_NOW') }}
      </button>
      <button
        type="button"
        class="text-[12px] text-lais-warning underline"
        :aria-label="t('LAIS_ACCOUNT.CONFIRM.CLOSE')"
        @click="dismissed = true"
      >
        {{ t('LAIS_ACCOUNT.CONFIRM.CLOSE') }}
      </button>
    </div>
  </div>
</template>
